import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Share2, Mail, Building2 } from "lucide-react";
import { toast } from "sonner";

export const MISSION_TEXT =
  "GeneticsTrade is the verified bio-exchange for livestock genetics. We connect accredited studs, AI centers, and heritage breeders with buyers worldwide — every lot carries verified genetic data, mandatory health certificates, and conservation-status flagging for rare breeds. Orders are escrow-protected (6% transparent sales fee) and shipped in LN2 cold-chain custody at -196°C with live temperature & GPS tracking.";

export default function InviteBusiness({ triggerClassName = "", size = "sm" }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const [refCode, setRefCode] = useState(null);
  useEffect(() => {
    if (open && user && user !== false) api.get("/referrals/me").then(r => setRefCode(r.data.code)).catch(() => {});
  }, [open, user]);
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const registerUrl = `${siteUrl}/register${refCode ? `?ref=${refCode}` : ""}`;
  const inviteText = `${MISSION_TEXT}\n\nList your genetics or services here: ${registerUrl}`;

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteText);
      toast.success("Invite copied — paste it anywhere");
    } catch {
      toast.error("Copy failed — select the text manually");
    }
  };

  const shareFacebook = () =>
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}&quote=${encodeURIComponent(MISSION_TEXT)}`, "_blank", "width=600,height=540");

  const shareEmail = () => {
    const subject = encodeURIComponent("You're invited to list on GeneticsTrade");
    const body = encodeURIComponent(inviteText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="invite-business-btn" variant="outline" size={size} className={triggerClassName}>
          <Building2 className="h-4 w-4 mr-1" /> Invite Business to List
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite a business to list on GeneticsTrade</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-stone-700 leading-relaxed bg-stone-50 border border-stone-200 rounded-lg p-4" data-testid="invite-mission-text">
          {MISSION_TEXT}
        </div>
        <div className="text-xs mono text-stone-500 break-all" data-testid="invite-link">{registerUrl}</div>
        {refCode && (
          <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2" data-testid="invite-credit-note">
            Your referral code <span className="mono font-semibold">{refCode}</span> is embedded — earn <strong>3% account credit on their first sale</strong>, then <strong>0.1% on every sale thereafter</strong>. Not redeemable for cash.
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          <Button data-testid="invite-copy-btn" className="btn-forest" onClick={copyInvite}>
            <Copy className="h-4 w-4 mr-1" /> Copy invite
          </Button>
          <Button data-testid="invite-facebook-btn" variant="outline" onClick={shareFacebook}>
            <Share2 className="h-4 w-4 mr-1" /> Facebook
          </Button>
          <Button data-testid="invite-email-btn" variant="outline" onClick={shareEmail}>
            <Mail className="h-4 w-4 mr-1" /> Email
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
